import { type CreditCardDialogContext } from '@cart-services/hooks/credit-card-dialog-types'
import { type AlipayDialogContext } from '@cart-services/hooks/use-alipay-dialog-flow-actions'
import { type ApplePayDialogContext } from '@cart-services/hooks/useApplePayDialogActions'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { type FortunePaymentExecutionResponse } from '@hooks-api/types/apiResponse'
import { type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { isCkoFlowModule } from '@payments'
import { PAYMENT_DIALOG_TYPE } from '@payments/lib/checkout-pay/payment-dialog-type'
import { getPaymentModulePrime, isNonFlowPaymentModule, type AppliedPaymentModule, type PaymentModulePrime } from '../utils/utility-process-pay-module'
import { useFpB2bCkoFlowPayment } from './useFpB2bCkoFlowPayment'

export interface FpB2bPaymentUrlBuilders {
  buildSuccessUrl: (paymentTransactionId: string) => string
  buildFallbackUrl: () => string
}

export interface UseFpB2bPaymentDispatchParams {
  paymentModule: AppliedPaymentModule | undefined
  urlBuilders: FpB2bPaymentUrlBuilders
  /** 只在取 prime 失敗時呼叫（該路徑會吞掉 error 並回傳 undefined）；其餘 error 一律 throw 給呼叫端 */
  onPaymentPrimeError: (error: unknown) => void
}

export interface FpB2bPaymentDispatchArgs {
  paymentTransactionId: string
  paymentOptionId: number | undefined
  /** dialog 失敗時的 datadog 關聯欄位，付款流程不使用 */
  orderHash?: string | null
  email?: string
  bankCode?: string | null
  userPaymentTokenInfo?: UserPaymentTokenInfo | null
  rememberPaymentCardToken?: boolean
}

export interface UseFpB2bPaymentDispatchResult {
  /** 回傳 undefined = 已交棒給 dialog、或取 prime 失敗（已呼叫 onPaymentPrimeError）；兩者呼叫端都不需再處理 redirect */
  dispatch: (args: FpB2bPaymentDispatchArgs) => Promise<FortunePaymentExecutionResponse | undefined>
  applePayDialogContext: ApplePayDialogContext | null
  alipayDialogContext: AlipayDialogContext | null
  creditCardDialogContext: CreditCardDialogContext | null
  closeApplePayDialog: () => void
  closeAlipayDialog: () => void
  closeCreditCardDialog: () => void
}

/**
 * B2B 付款觸發唯一入口，`/checkout` 與 `/retry-payment/:id` 共用。依付款模組分流：
 * - CKO Flow module → 依 `getPaymentDialogType()` 開對應彈窗
 * - 其餘（Tappay / ECPay / CKO Frames / 已存卡）→ prime + execute
 */
export const useFpB2bPaymentDispatch = ({
  paymentModule,
  urlBuilders,
  onPaymentPrimeError,
}: UseFpB2bPaymentDispatchParams): UseFpB2bPaymentDispatchResult => {
  const { trigger: executePayment } = useApiFpPaymentExecution()
  const {
    applePayDialogContext,
    openApplePayDialog,
    closeApplePayDialog,
    alipayDialogContext,
    openAlipayDialog,
    closeAlipayDialog,
    creditCardDialogContext,
    openCreditCardDialogForTransaction,
    closeCreditCardDialog,
  } = useFpB2bCkoFlowPayment({ paymentModule })

  const dispatch = useCallbackEvent(async (args: FpB2bPaymentDispatchArgs): Promise<FortunePaymentExecutionResponse | undefined> => {
    const paymentSuccessUrl = urlBuilders.buildSuccessUrl(args.paymentTransactionId)
    const paymentFallbackUrl = urlBuilders.buildFallbackUrl()

    // 不在此打 init：dialog 掛載後自行觸發，這裡先打會換掉 session
    if (isCkoFlowModule(paymentModule)) {
      const dialogType = paymentModule.getPaymentDialogType()
      const paymentOptionId = args.paymentOptionId ?? 0
      if (dialogType === PAYMENT_DIALOG_TYPE.APPLE_PAY) {
        openApplePayDialog({
          paymentTransactionId: args.paymentTransactionId,
          orderHash: args.orderHash ?? null,
          paymentSuccessUrl,
          paymentFallbackUrl,
          paymentOptionId,
        })
        return undefined
      }
      if (dialogType === PAYMENT_DIALOG_TYPE.ALIPAY_HK) {
        openAlipayDialog({
          paymentTransactionId: args.paymentTransactionId,
          orderHash: args.orderHash ?? null,
          paymentSuccessUrl,
          paymentFallbackUrl,
          paymentOptionId,
          email: args.email,
        })
        return undefined
      }
      if (dialogType === PAYMENT_DIALOG_TYPE.CREDIT_CARD) {
        openCreditCardDialogForTransaction({
          paymentTransactionId: args.paymentTransactionId,
          orderHash: args.orderHash ?? null,
          paymentSuccessUrl,
          paymentFallbackUrl,
          paymentOptionId,
          email: args.email,
          rememberPaymentCardToken: args.rememberPaymentCardToken,
        })
        return undefined
      }
      // merchant 目前只開放 Apple Pay / AliPayHK / 信用卡三種 Flow 彈窗（如 WeChat Pay HK）尚未在 UI 開放選項，
      // 故意 throw 而非 fallback：避免商戶端未來加開付款方式時，這裡悄悄降級成通用流程卻沒人注意到。
      throw new Error(`[useFpB2bPaymentDispatch] unsupported Flow module: ${paymentModule.constructor.name}`)
    }

    // token-based（Tappay / ECPay / CKO Frames / 已存卡）
    let prime: PaymentModulePrime | null = null
    try {
      prime = !args.userPaymentTokenInfo && isNonFlowPaymentModule(paymentModule)
        ? await getPaymentModulePrime(paymentModule)
        : null
    }
    catch (error) {
      onPaymentPrimeError(error)
      return undefined
    }

    const { data: paymentResult } = await executePayment({
      payment_transaction_id: Number(args.paymentTransactionId),
      service_pay_info: {
        bank_code: args.bankCode,
        apple_pay_info: prime && 'applePayInfo' in prime ? prime.applePayInfo : null,
        pay_token: prime && 'applePayInfo' in prime ? prime.applePayInfo.paymentData.data : prime?.paymentPrime,
        card_identifier: prime && 'paymentPrime' in prime ? prime.cardIdentifier : null,
      },
      user_payment_token_info: args.userPaymentTokenInfo,
      payment_success_url: paymentSuccessUrl,
      payment_fallback_url: paymentFallbackUrl,
      payment_option_id: args.paymentOptionId,
    })
    return paymentResult
  })

  return {
    dispatch,
    applePayDialogContext,
    alipayDialogContext,
    creditCardDialogContext,
    closeApplePayDialog,
    closeAlipayDialog,
    closeCreditCardDialog,
  }
}
